'use client';

import React from 'react';
import { useWizard } from '@app/app/_components/Wizard/useWizard';
import { Wizard, WizardStep } from '@app/app/_components/Wizard/Wizard';
import { GatewayRedirectStep } from '@app/app/esales/transport/_sections/steps/GatewayRedirectStep';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { useBeforeUnload } from '@app/lib/use-before-unload/useBeforeUnload';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useEffectOnce } from '@app/lib/use-change-effect/useEffectOnce';
import { clearSessionId } from '@app/infrastructure/eshop/clearSessionId';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormState } from 'react-dom';
import { scrollToTop } from '@app/lib/scroll-to-top/scrollToTop';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { Client } from '@app/domain/model/Client';
import { CommonType } from '@app/domain/model/CommonType';
import { useHomeInsuranceDiscounts } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/hooks/useHomeInsuranceDiscounts';
import { createEnhancedFormAction } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/helpers/createEnhancedFormAction';
import { buildSteps } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/helpers/buildSteps';
import { HomeInsuranceRequestResult } from '@app/service/eshop/home/actions/requestHomeInsurance';
import { PurchaseHomeInsuranceResult } from '@app/service/eshop/home/actions/purchaseHomeInsurance';
import { HomeInsuranceLandingSection } from '@app/app/esales/home/_sections/HomeInsuranceLandingSection/HomeInsuranceLandingSection';
import { HomeInsuranceWizardFooter } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/HomeInsuranceWizardFooter';
import { ProductDataLeasing } from '@app/domain/model/eshop/transport/ProductDataLeasing';
import { ProductDataObject } from '@app/domain/model/eshop/home/ProductDataObject';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { HomeInsurancePremiumResponse } from '@app/domain/model/eshop/home/HomeInsurancePremiumResponse';
import { usePriceCalculation } from '@app/app/esales/home/_sections/setups/usePriceCalculation';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { HOME_INSURANCE_STEPS } from '@app/app/esales/home/_sections/steps/HOME_INSURANCE_STEPS';
import { buildStepValidationData } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/helpers/buildStepValidationData';
import { Schema, ValidationError } from 'yup';
import { buildViolations } from '@app/lib/form/buildViolations';
import { GlobalLoader } from '@app/app/_components/GlobalLoader/GlobalLoader';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { usePolicyholderFormFields } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/hooks/usePolicyholderFormFields';
import { Tenant } from '@app/infrastructure/tenant/model/Tenant';
import { PolicyholderFormFields } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/hooks/usePolicyholderFormFields';
import { calculateHomePremiums } from '@app/service/eshop/home/actions/calculateHomePremiums';
import { logger } from '@app/infrastructure/logger/logger';
import { twMerge } from 'tailwind-merge';
import { EsalesTransportFooter } from '@app/app/esales/transport/components/EsalesTransportFooter';
import { useHomeGtmTracking } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/helpers/useHomeGtmTracking';
import { trackStartCalculation } from '@app/infrastructure/gtm/home/trackStartCalculation';
import { trackProposalCalculated } from '@app/infrastructure/gtm/home/trackProposalCalculated';
import { formatAddress } from '@app/lib/address-formatter/formatAddress';

interface Props {
    backgroundImageSrc: string;
    mobileImageSrc: string;
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
    authState: AuthState;
    clients: Client[];
    swedbankLogoSrc: string;
    sebLogoSrc: string;
    leasings: ProductDataLeasing[];
    objects: ProductDataObject[];
    purposes: Nullable<ProductDataPurpose>;
    riders: ProductDataRider[];
    tenant: Tenant;
    initialPurchaseResult?: Nullable<PurchaseHomeInsuranceResult>;
    initialStep?: Nullable<string>;
    requestData?: Nullable<HomeInsuranceRequestResult>;
}

export function HomeInsuranceWizardSection({
    backgroundImageSrc,
    mobileImageSrc,
    stakeholderLogoSrc,
    vigLogoSrc,
    authState,
    clients,
    swedbankLogoSrc,
    sebLogoSrc,
    leasings,
    objects,
    purposes,
    riders,
    tenant,
    initialPurchaseResult,
    initialStep,
    requestData,
}: Props) {
    const t = useTranslations();
    const { settings } = useAppParameters();

    const isReinitializedSession = initialPurchaseResult != null && initialStep != null && requestData != null;
    const formRef = useRef<HTMLFormElement>(null);
    const setCurrentStepKeyRef = useRef<Nullable<(key: string) => void>>(null);

    const [requestResult, setRequestResult] = useState<Nullable<HomeInsuranceRequestResult>>(requestData ?? null);
    const [currentPremiums, setCurrentPremiums] = useState<Nullable<HomeInsurancePremiumResponse>>(
        initialPurchaseResult?.premiums ?? null,
    );

    const {
        fields: policyholderFields,
        handlers: policyholderHandlers,
        setFields,
        setDefaultAddress,
    } = usePolicyholderFormFields(authState, requestResult);

    const [clientValidationErrors, setClientValidationErrors] = useState<Nullable<ViolationMap>>(null);
    const [clientValidationErrorMessage, setClientValidationErrorMessage] = useState<Nullable<string>>(null);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [loaderText, setLoaderText] = useState('');

    const [finished, setFinished] = useState(false);
    const [wizardStarted, setWizardStarted] = useState(isReinitializedSession);
    const [discountCodeData, setDiscountCodeData] = useState<DiscountCodeData>({
        discountCode: '',
        isInvalid: false,
        isSuccess: false,
        validTravelCode: undefined,
    });
    const [pledgedToBank, setPledgedToBank] = useState<Nullable<string>>(
        initialPurchaseResult?.formData?.pledgedToBank ?? null,
    );
    const [selectedLeasing, setSelectedLeasing] = useState<Nullable<string>>(
        initialPurchaseResult?.formData?.leasing ?? null,
    );
    const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<Nullable<string>>(null);

    const { handleDiscountCode, handleApplyDiscountClick } = useHomeInsuranceDiscounts(
        discountCodeData,
        setDiscountCodeData,
    );

    const [paymentFrequency, setPaymentFrequency] = useState(1);

    const initialSelectedRiskCode = initialPurchaseResult?.formData?.homeInsuranceRisk ?? '';

    const initialSelectedRiskPackageCodes = useMemo(() => {
        const savedRiskPackageCodes = initialPurchaseResult?.formData?.selectedRiders
            ?.map(rider => rider.riskPackageCode)
            .filter(code => code !== '');
        return savedRiskPackageCodes ?? [];
    }, [initialPurchaseResult?.formData?.selectedRiders]);

    const { priceState, handleRisksChange, handleRidersChange } = usePriceCalculation(
        currentPremiums,
        riders,
        initialSelectedRiskCode,
        initialSelectedRiskPackageCodes,
    );

    const { trackStepCompletion } = useHomeGtmTracking({
        selectedRisk: priceState.selectedRisk ?? null,
        selectedRiders: priceState.selectedRiders,
    });

    const handlePledgedToBankChange = useCallback((value: string) => {
        setPledgedToBank(value);

        if (value === CommonType.No) {
            setSelectedLeasing(null);
        }
    }, []);

    const handleSelectedLeasingChange = useCallback((value: string) => {
        setSelectedLeasing(value);
    }, []);

    const handlePaymentMethodTypeChange = useCallback((paymentMethodType: Nullable<string>) => {
        setSelectedPaymentMethodType(paymentMethodType);
    }, []);

    const handleSavePolicyholder = useCallback(
        (data: PolicyholderFormFields) => {
            setFields(data);
        },
        [setFields],
    );

    const editObjectSummary = useCallback(() => {
        setCurrentStepKeyRef.current?.(
            requestResult?.propertyInfo != null && requestResult.acceptedToRecieveInfoFromRegisterCenter
                ? HOME_INSURANCE_STEPS.OBJECT_SUMMARY_READ_ONLY
                : HOME_INSURANCE_STEPS.OBJECT_SUMMARY_EDITABLE,
        );
        setWizardStarted(false);
    }, [requestResult]);

    const editPolicy = useCallback(() => {
        setCurrentStepKeyRef.current?.(HOME_INSURANCE_STEPS.CHOOSE_INSURANCE);
    }, []);

    const editBeginDate = useCallback(() => {
        setCurrentStepKeyRef.current?.(HOME_INSURANCE_STEPS.INSURANCE_BEGIN_DATE);
    }, []);

    const editAdditionalRisks = useCallback(() => {
        setCurrentStepKeyRef.current?.(HOME_INSURANCE_STEPS.ADDITIONAL_RISKS);
    }, []);

    const onSuccessfulLogin = useCallback(() => {
        setCurrentStepKeyRef.current?.(HOME_INSURANCE_STEPS.POLICYHOLDER_INFORMATION);
    }, []);

    const defaultConstructionType = purposes?.defaultPurpose;

    const enhancedFormAction = createEnhancedFormAction(
        discountCodeData,
        requestResult,
        defaultConstructionType ?? null,
        priceState.selectedRiders,
        pledgedToBank,
        selectedLeasing,
        leasings,
        isReinitializedSession,
    );

    const [formState, formAction] = useFormState(enhancedFormAction, {
        status: FormStatus.Pending,
        data: initialPurchaseResult ?? undefined,
    });

    const mergedFormState = useMemo(() => {
        if (!clientValidationErrors) {
            return formState;
        }

        return {
            ...formState,
            violations: {
                ...formState.violations,
                ...clientValidationErrors,
            },
        };
    }, [formState, clientValidationErrors]);

    const handlePaymentFrequencyChange = useCallback(
        async (newPaymentFrequency: number) => {
            if (!requestResult) return;

            setIsRecalculating(true);
            setLoaderText(t('eshop.home.calculatingPremiums'));
            setPaymentFrequency(newPaymentFrequency);

            try {
                const updatedPremiums = await calculateHomePremiums(
                    requestResult,
                    newPaymentFrequency,
                    formState.data?.formData,
                    priceState.selectedRiskCode,
                );

                setCurrentPremiums(updatedPremiums);
            } catch (error) {
                logger.logException(error);
            } finally {
                setIsRecalculating(false);
                setLoaderText('');
            }
        },
        [requestResult, t, formState.data?.formData, priceState.selectedRiskCode],
    );

    const steps = useMemo<WizardStep[]>(
        () =>
            buildSteps({
                t,
                tenant,
                requestResult,
                discountCodeData,
                handleDiscountCode,
                handleApplyDiscountClick,
                leasings,
                handlePledgedToBankChange,
                pledgedToBank,
                handleSelectedLeasingChange,
                selectedLeasing,
                formState: mergedFormState,
                objects,
                purposes,
                selectedRiskCode: priceState.selectedRiskCode,
                handleRisksChange,
                selectedRisk: priceState.selectedRisk ?? null,
                riders,
                selectedRiders: priceState.selectedRiders,
                handleRidersChange,
                policyholderFields,
                policyholderHandlers,
                onSavePolicyholder: handleSavePolicyholder,
                totalPrice: priceState.totalPrice,
                priceToPayNow: priceState.priceToPayNow,
                paymentFrequency,
                onPaymentFrequencyChange: handlePaymentFrequencyChange,
                editObjectSummary,
                editPolicy,
                editBeginDate,
                editAdditionalRisks,
                handlePaymentMethodTypeChange,
                selectedPaymentMethodType,
                currentPremiums,
                authState,
                clients,
                swedbankLogoSrc,
                sebLogoSrc,
                onSuccessfulLogin,
                settings,
                initialBeginDate: formState.data?.formData?.beginDate,
            }),
        [
            t,
            tenant,
            requestResult,
            discountCodeData,
            handleDiscountCode,
            handleApplyDiscountClick,
            leasings,
            handlePledgedToBankChange,
            pledgedToBank,
            handleSelectedLeasingChange,
            selectedLeasing,
            mergedFormState,
            objects,
            purposes,
            priceState.selectedRiskCode,
            priceState.selectedRisk,
            priceState.selectedRiders,
            priceState.totalPrice,
            priceState.priceToPayNow,
            handleRisksChange,
            riders,
            handleRidersChange,
            policyholderFields,
            policyholderHandlers,
            handleSavePolicyholder,
            paymentFrequency,
            handlePaymentFrequencyChange,
            editObjectSummary,
            editPolicy,
            editBeginDate,
            editAdditionalRisks,
            handlePaymentMethodTypeChange,
            selectedPaymentMethodType,
            currentPremiums,
            authState,
            clients,
            swedbankLogoSrc,
            sebLogoSrc,
            onSuccessfulLogin,
            settings,
            formState.data?.formData?.beginDate,
        ],
    );

    const {
        filteredSteps,
        currentSubStepIndex,
        currentStepIndex,
        currentStepKey,
        isFinalStep,
        isFirstStep,
        next,
        back,
        setCurrentStepKey,
    } = useWizard(steps);

    useChangeEffect(() => {
        setCurrentStepKeyRef.current = setCurrentStepKey;
    }, setCurrentStepKey);

    useEffectOnce(() => {
        if (initialStep) {
            setCurrentStepKey(initialStep);
        }
    });

    useChangeEffect(() => {
        if (
            formState.data?.premiums &&
            (currentStepKey === HOME_INSURANCE_STEPS.OBJECT_SUMMARY_EDITABLE ||
                currentStepKey === HOME_INSURANCE_STEPS.OBJECT_SUMMARY_READ_ONLY ||
                currentStepKey === HOME_INSURANCE_STEPS.SUMMARY)
        ) {
            setCurrentPremiums(formState.data.premiums);
        }
    }, formState.data?.premiums);

    useChangeEffect(async () => {
        if (!requestResult) return;

        if (currentStepKey === HOME_INSURANCE_STEPS.CHOOSE_INSURANCE && currentPremiums?.riskPackages.length === 1) {
            setIsRecalculating(true);
            setLoaderText(t('eshop.home.calculatingPremiums'));

            try {
                const updatedPremiums = await calculateHomePremiums(requestResult, 1, formState.data?.formData);

                setCurrentPremiums(updatedPremiums);
            } catch (error) {
                logger.logException(error);
            } finally {
                setIsRecalculating(false);
                setLoaderText('');
            }
        }

        if (
            currentStepKey === HOME_INSURANCE_STEPS.SUMMARY &&
            currentPremiums &&
            currentPremiums.riskPackages.length > 1 &&
            formState.data?.formData.paymentCount !== '1'
        ) {
            setIsRecalculating(true);
            setLoaderText(t('eshop.home.calculatingPremiums'));

            try {
                const updatedPremiums = await calculateHomePremiums(
                    requestResult,
                    parseInt(formState.data?.formData.paymentCount || '1', 10),
                    formState.data?.formData,
                    priceState.selectedRiskCode,
                );

                setCurrentPremiums(updatedPremiums);
            } catch (error) {
                logger.logException(error);
            } finally {
                setIsRecalculating(false);
                setLoaderText('');
            }
        }
    }, currentStepKey);

    useBeforeUnload(finished, t('general.areYouSureYouWantToLeave'));

    const handleBack = useCallback(() => {
        if (!wizardStarted) {
            confirm(t('general.areYouSureYouWantToLeave'));
        }

        if (isFirstStep && wizardStarted) {
            setWizardStarted(false);
        }

        formState.status = FormStatus.Pending;
        back();
    }, [back, formState, isFirstStep, t, wizardStarted]);

    const validateStepAndSubmit = useCallback(
        async (schema: Schema, formDataObject: Record<string, unknown>, loaderTextKey: string) => {
            try {
                await schema.validate(formDataObject, {
                    abortEarly: false,
                    context: { isPropertyDataFound: requestResult?.propertyInfo != null },
                });

                setIsRecalculating(true);
                setLoaderText(t(loaderTextKey));

                formRef.current?.requestSubmit();
            } catch (error) {
                if (error instanceof ValidationError) {
                    const violations = buildViolations(error, 'general.validationError');
                    setClientValidationErrors(violations.violations ?? null);
                    setClientValidationErrorMessage('general.validationError');
                    scrollToTop();
                }
            }
        },
        [t, requestResult?.propertyInfo],
    );

    const handleSubmit = useCallback(async () => {
        setClientValidationErrors(null);
        setClientValidationErrorMessage(null);

        const formData = new FormData(formRef.current!);
        const stepValidationData = buildStepValidationData(currentStepKey, formData);

        if (!stepValidationData) {
            formRef.current?.requestSubmit();
            return;
        }

        await validateStepAndSubmit(
            stepValidationData.schema,
            stepValidationData.data,
            stepValidationData.loaderTextKey,
        );
    }, [currentStepKey, validateStepAndSubmit]);

    const handleStartWizard = useCallback(
        (result: HomeInsuranceRequestResult) => {
            setRequestResult(result);
            if (result.address) {
                setDefaultAddress(result.address);
            }
            setWizardStarted(true);
            trackStartCalculation({
                address: formatAddress(result.address),
            });
        },
        [setDefaultAddress],
    );

    useChangeEffect(async () => {
        if (formState.status === FormStatus.Success) {
            setIsRecalculating(false);
            setClientValidationErrors(null);
            setClientValidationErrorMessage(null);

            if (currentStepKey === HOME_INSURANCE_STEPS.SUMMARY && formState.data?.offers) {
                trackProposalCalculated({
                    offers: formState.data.offers,
                    selectedRisk: priceState.selectedRisk ?? null,
                    selectedRiders: priceState.selectedRiders,
                });
            }

            trackStepCompletion({ currentStepKey });

            if (isFinalStep) {
                await clearSessionId();
                setFinished(true);
            } else {
                next();
            }
        }

        if (formState.status === FormStatus.Error) {
            scrollToTop();
        }

        setIsRecalculating(false);
    }, formState);

    useChangeEffect(() => {
        scrollToTop();
    }, currentStepKey);

    const getStepMarginClass = useCallback(() => {
        const stepMargins: Record<string, string> = {
            [HOME_INSURANCE_STEPS.CHOOSE_INSURANCE]: '2xl:mb-[850px]',
            [HOME_INSURANCE_STEPS.INSURANCE_BEGIN_DATE]: '2xl:mb-[850px]',
            [HOME_INSURANCE_STEPS.ADDITIONAL_RISKS]: '2xl:mb-[850px]',
            [HOME_INSURANCE_STEPS.PAYMENT.OPTIONS]: '2xl:mb-[850px]',
            [HOME_INSURANCE_STEPS.PAYMENT.METHOD_TYPE]: '2xl:mb-[850px]',
        };

        return stepMargins[currentStepKey] || '';
    }, [currentStepKey]);

    if (finished) {
        return <GatewayRedirectStep checkoutResult={formState.data?.checkoutResult} />;
    }

    return (
        <>
            <GlobalLoader isVisible={isRecalculating} text={loaderText} />

            {!wizardStarted && (
                <HomeInsuranceLandingSection
                    onSuccess={handleStartWizard}
                    backgroundImageSrc={backgroundImageSrc}
                    mobileImageSrc={mobileImageSrc}
                    stakeholderLogoSrc={stakeholderLogoSrc}
                    vigLogoSrc={vigLogoSrc}
                    personalCode={requestResult?.policyHolderCode}
                    address={requestResult?.address}
                    propertyIsEligibleForInsurance={requestResult?.isEligibleForInsurance}
                    acceptedPrivacyPolicy={requestResult?.acceptedPrivacyPolicy}
                    acceptedToRecieveInfoFromRegisterCenter={requestResult?.acceptedToRecieveInfoFromRegisterCenter}
                />
            )}

            {wizardStarted && (
                <form ref={formRef} action={formAction}>
                    {!isFinalStep && <input type="hidden" name="step" value={currentStepKey} />}

                    <Wizard
                        isPercentageStepper
                        steps={filteredSteps}
                        currentStepIndex={currentStepIndex}
                        currentSubStepIndex={currentSubStepIndex}
                        onSubmit={handleSubmit}
                        onBack={handleBack}
                        className={twMerge('mb-[400px] flex-1', getStepMarginClass())}
                        contentFooter={
                            <div className="mb-12">
                                <EsalesTransportFooter
                                    stakeholderLogoSrc={stakeholderLogoSrc}
                                    vigLogoSrc={vigLogoSrc}
                                />
                            </div>
                        }
                        error={
                            clientValidationErrorMessage
                                ? t(clientValidationErrorMessage)
                                : formState.status === FormStatus.Error
                                  ? t(formState.message)
                                  : undefined
                        }
                        footer={
                            <HomeInsuranceWizardFooter
                                formState={formState}
                                totalPrice={priceState.totalPrice}
                                currentStepKey={currentStepKey}
                            />
                        }
                    />
                </form>
            )}
        </>
    );
}
